/**
 * App.World - 世界数据读写 API（多楼层）
 */

import * as THREE from 'three';
import type { Floor, Room, End, Vertex, Cell, ShaftColumn, Shaft, TileType } from '../types';
import { TileType as T } from '../types';
import { App } from './App';

/* ─────────────── Module: World（世界数据读写 API，多楼层）─────────────── */
App.World = {
  /**
   * 检查坐标是否在网格内
   */
  inB(x: number, z: number): boolean {
    const G = App.Config?.GRID || { W: 26, D: 18 };
    return x >= 0 && x < G.W && z >= 0 && z < G.D;
  },

  /**
   * 世界坐标：活动层（yBase随当前楼层）
   */
  g2w(x: number, z: number): THREE.Vector3 {
    const G = App.Config?.GRID || { W: 26, D: 18 };
    const S = App.Store;
    return new THREE.Vector3(
      x - G.W / 2 + 0.5,
      S.f ? S.f.yBase : 0,
      z - G.D / 2 + 0.5
    );
  },

  /**
   * 世界坐标：指定 yBase（多楼层渲染用）
   */
  g2wy(x: number, z: number, yBase: number): THREE.Vector3 {
    const G = App.Config?.GRID || { W: 26, D: 18 };
    return new THREE.Vector3(
      x - G.W / 2 + 0.5,
      yBase,
      z - G.D / 2 + 0.5
    );
  },

  /**
   * 世界坐标：指定楼层
   */
  g2wF(floor: Floor, x: number, z: number): THREE.Vector3 {
    return this.g2wy(x, z, floor.yBase);
  },

  /**
   * 判断格子是否可走
   */
  walkable(x: number, z: number, floor?: Floor): boolean {
    const f = floor || App.Store.f;
    if (!f) return false;
    return this.inB(x, z) && f.type[x][z] !== T.FORBID;
  },

  /**
   * 获取格子类型
   */
  cellType(x: number, z: number, floor?: Floor): TileType | undefined {
    const f = floor || App.Store.f;
    if (!f) return undefined;
    return f.type[x][z];
  },

  /**
   * 根据索引获取楼层
   */
  floorOfIdx(fi: number): Floor | null {
    return App.Store.floors[fi] || null;
  },

  /**
   * 获取楼层名称
   */
  floorName(fi: number): string {
    const f = this.floorOfIdx(fi);
    return f ? f.name : '—';
  },

  // ════════════════════════════════════════════════════════════════════
  // 机房相关
  // ════════════════════════════════════════════════════════════════════

  /**
   * 获取机房占用的所有格子
   */
  roomCells(r: Room): Cell[] {
    return r.shape.map(([dx, dz]) => ({ x: r.x + dx, z: r.z + dz }));
  },

  /**
   * 获取机房在指定锚点时的所有格子
   */
  roomCellsAt(r: Room, ax: number, az: number): Cell[] {
    return r.shape.map(([dx, dz]) => ({ x: ax + dx, z: az + dz }));
  },

  /**
   * 获取所有机房的所有格子
   */
  allRoomCells(): Cell[] {
    const a: Cell[] = [];
    for (const r of App.Store.f.rooms) {
      for (const c of this.roomCells(r)) {
        a.push(c);
      }
    }
    return a;
  },

  /**
   * 查找格子上的机房
   */
  roomAtCell(cell: Cell): Room | null {
    return App.Store.f.rooms.find(r =>
      this.roomCells(r).some(c => c.x === cell.x && c.z === cell.z)
    ) || null;
  },

  /**
   * 计算机房形状的边界
   */
  shapeBounds(shape: [number, number][]): { mnx: number; mxx: number; mnz: number; mxz: number } {
    let mnx = 0, mxx = 0, mnz = 0, mxz = 0;
    for (const [dx, dz] of shape) {
      mnx = Math.min(mnx, dx);
      mxx = Math.max(mxx, dx);
      mnz = Math.min(mnz, dz);
      mxz = Math.max(mxz, dz);
    }
    return { mnx, mxx, mnz, mxz };
  },

  /**
   * 限制机房锚点在网格内
   */
  clampAnchor(r: Room, ax: number, az: number): { x: number; z: number } {
    const b = this.shapeBounds(r.shape);
    const G = App.Config?.GRID || { W: 26, D: 18 };
    return {
      x: Math.max(-b.mnx, Math.min(ax, G.W - 1 - b.mxx)),
      z: Math.max(-b.mnz, Math.min(az, G.D - 1 - b.mxz))
    };
  },

  /**
   * 提交机房格子（标记为机房类型）
   */
  commitRoomCells(r: Room): void {
    const T = App.Config?.T || { ROOM: 2 };
    const f = App.Store.f;
    for (const [dx, dz] of r.shape) {
      const x = r.x + dx, z = r.z + dz;
      if (!this.inB(x, z)) continue;
      const k = x + ',' + z;
      if (!f.roomOrig.has(k)) f.roomOrig.set(k, f.type[x][z]);
      f.type[x][z] = T.ROOM;
    }
  },

  /**
   * 擦除机房格子
   */
  eraseRoomCells(r: Room): void {
    const T = App.Config?.T || { ROOM: 2, WALK: 0 };
    const f = App.Store.f;
    for (const c of this.roomCells(r)) {
      const k = c.x + ',' + c.z;
      f.type[c.x][c.z] = f.roomOrig.has(k) ? f.roomOrig.get(k)! : T.WALK;
      f.roomOrig.delete(k);
    }
  },

  /**
   * 判断是否可以在指定位置放置机房
   */
  canPlaceAt(r: Room, ax: number, az: number): boolean {
    const T = App.Config?.T || { ROOM: 2, END: 3 };
    const f = App.Store.f;
    const occ = new Set<string>();
    for (const o of f.rooms) {
      if (o === r) continue;
      for (const c of this.roomCells(o)) {
        occ.add(c.x + ',' + c.z);
      }
    }
    for (const [dx, dz] of r.shape) {
      const x = ax + dx, z = az + dz;
      if (!this.inB(x, z)) return false;
      if (f.type[x][z] === T.END) return false;
      if (occ.has(x + ',' + z)) return false;
    }
    return true;
  },

  // ════════════════════════════════════════════════════════════════════
  // 连线相关
  // ════════════════════════════════════════════════════════════════════

  /**
   * 将连线格子应用到网格
   */
  applyLineToGrid(cells: Cell[]): void {
    const T = App.Config?.T || { WALK: 0, ROOM: 2, END: 3 };
    const f = App.Store.f;
    for (const c of cells) {
      if (!this.inB(c.x, c.z)) continue;
      const t = f.type[c.x][c.z];
      if (t === T.ROOM || t === T.END) continue;
      const k = c.x + ',' + c.z;
      if (!f.drawnOrig.has(k)) f.drawnOrig.set(k, t);
      f.type[c.x][c.z] = T.WALK;
      f.drawnCells.add(k);
    }
  },

  /**
   * 重新计算交接点
   */
  recomputeJunctions(): void {
    const f = App.Store.f;
    const cnt = new Map<string, number>();
    f.junctions = [];
    for (const line of f.lines) {
      const local = new Set(line.cells.map(c => c.x + ',' + c.z));
      for (const k of local) {
        cnt.set(k, (cnt.get(k) || 0) + 1);
      }
    }
    for (const [k, n] of cnt) {
      if (n >= 2) {
        const [x, z] = k.split(',').map(Number);
        f.junctions.push({ x, z });
      }
    }
  },

  /**
   * 线性插值格子
   */
  lerpCells(a: Cell, b: Cell): Cell[] {
    const cells: Cell[] = [];
    const sx = Math.sign(b.x - a.x), sz = Math.sign(b.z - a.z);
    let x = a.x, z = a.z;
    cells.push({ x, z });
    while (x !== b.x) { x += sx; cells.push({ x, z }); }
    while (z !== b.z) { z += sz; cells.push({ x, z }); }
    return cells;
  },

  /**
   * 栅格化顶点为格子
   */
  rasterizeVertices(verts: Vertex[]): Cell[] {
    const all: Cell[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < verts.length - 1; i++) {
      for (const c of this.lerpCells(verts[i], verts[i + 1])) {
        const k = c.x + ',' + c.z;
        if (!seen.has(k)) {
          seen.add(k);
          all.push(c);
        }
      }
    }
    return all;
  },

  // ════════════════════════════════════════════════════════════════════
  // 竖井（立管）：贯通全楼，在所有可放置层同位放置 RISER
  // ════════════════════════════════════════════════════════════════════

  /**
   * 放置竖井（仅贯穿指定楼层范围 [from, to]）
   * 向注册表登记编号，并把 RISER 标记打到范围内的楼层格子上
   */
  placeShaft(x: number, z: number, from: number, to: number, systemId?: number): void {
    const T = App.Config?.T || { ROOM: 2, END: 3, RISER: 4, WALK: 0 };
    const S = App.Store;
    if (!this.inB(x, z)) return;

    const f0 = Math.max(0, Math.min(from, to));
    const f1 = Math.min(S.floors.length - 1, Math.max(from, to));

    for (let fi = f0; fi <= f1; fi++) {
      const f = S.floors[fi];
      if (!f) continue;
      const t = f.type[x][z];
      if (t === T.ROOM || t === T.END || t === T.RISER) continue;
      const k = x + ',' + z;
      if (!f.riserOrig.has(k)) f.riserOrig.set(k, t);
      f.type[x][z] = T.RISER;
    }

    S.shaftCounter++;
    S.shafts.push({ id: S.shaftCounter, x, z, floorFrom: f0, floorTo: f1, systemId: systemId != null ? systemId : S.activeSystemId });
  },

  /**
   * 移除竖井（按 (x,z) 定位，从注册表删除并还原范围内格子）
   */
  removeShaft(x: number, z: number): void {
    const T = App.Config?.T || { RISER: 4, WALK: 0 };
    const S = App.Store;
    const shaft = this.shaftAtCell(x, z);
    if (!shaft) return;

    for (let fi = shaft.floorFrom; fi <= shaft.floorTo; fi++) {
      const f = S.floors[fi];
      if (!f) continue;
      if (f.type[x][z] !== T.RISER) continue;
      const k = x + ',' + z;
      f.type[x][z] = f.riserOrig.has(k) ? f.riserOrig.get(k)! : T.WALK;
      f.riserOrig.delete(k);
    }

    S.shafts = S.shafts.filter(s => !(s.x === x && s.z === z));
  },

  /**
   * 按 (x,z) 查找注册表中的竖井对象（忽略楼层）
   */
  shaftAtCell(x: number, z: number): Shaft | null {
    return App.Store.shafts.find(s => s.x === x && s.z === z) || null;
  },

  /**
   * 判断指定位置是否有竖井（查注册表，跨楼层）
   */
  hasShaftAt(x: number, z: number): boolean {
    if (!this.inB(x, z)) return false;
    return !!this.shaftAtCell(x, z);
  },

  /**
   * 聚合所有竖井为贯通立柱数据（读注册表）
   */
  shaftColumns(): ShaftColumn[] {
    const C = App.Config;
    const S = App.Store;
    const cols: ShaftColumn[] = [];

    for (const s of S.shafts) {
      const ids: number[] = [];
      const ys: number[] = [];
      for (let fi = s.floorFrom; fi <= s.floorTo; fi++) {
        const f = S.floors[fi];
        if (!f) continue;
        ids.push(fi);
        ys.push(f.yBase);
      }
      if (!ys.length) continue;
      cols.push({
        id: s.id,
        x: s.x,
        z: s.z,
        yMin: Math.min(...ys),
        yMax: Math.max(...ys),
        floorIds: ids,
        systemId: s.systemId
      });
    }
    return cols;
  },

  /**
   * 根据注册表重建所有楼层的 RISER 格子缓存。
   * 先还原所有 RISER 格子（用 riserOrig），再按 shafts 重新打标。
   * 用于布局重生成、楼层增删、导入后保证网格与注册表一致。
   */
  reapplyShafts(): void {
    const T = App.Config?.T || { RISER: 4, ROOM: 2, END: 3, WALK: 0 };
    const S = App.Store;

    // 1. 清掉所有楼层的 RISER 格子（还原 riserOrig）
    for (const f of S.floors) {
      for (const [k, orig] of f.riserOrig) {
        const [x, z] = k.split(',').map(Number);
        if (this.inB(x, z)) f.type[x][z] = orig;
      }
      f.riserOrig.clear();
    }

    // 2. 按注册表重新打标（floorFrom/floorTo 越界时夹紧）
    for (const s of S.shafts) {
      const f0 = Math.max(0, s.floorFrom);
      const f1 = Math.min(S.floors.length - 1, s.floorTo);
      for (let fi = f0; fi <= f1; fi++) {
        const f = S.floors[fi];
        if (!f || !this.inB(s.x, s.z)) continue;
        const t = f.type[s.x][s.z];
        if (t === T.ROOM || t === T.END) continue; // 机房/末端优先
        const k = s.x + ',' + s.z;
        if (!f.riserOrig.has(k)) f.riserOrig.set(k, t);
        f.type[s.x][s.z] = T.RISER;
      }
    }
  },

  // ════════════════════════════════════════════════════════════════════
  // 布局生成
  // ════════════════════════════════════════════════════════════════════

  /**
   * 生成可走管空间（type矩阵）
   */
  genWalkable(f: Floor, li: number): void {
    const C = App.Config;
    const G = C.GRID;
    const T = C.T;
    const W = G.W, D = G.D;

    f.type = [];
    for (let x = 0; x < W; x++) {
      f.type.push(new Array(D).fill(T.WALK));
    }

    const wall = 2;

    if (li === 0) {
      // 环形通道
      for (let x = 0; x < W; x++) {
        for (let z = 0; z < D; z++) {
          const onFrame = x < wall || x >= W - wall || z < wall || z >= D - wall;
          f.type[x][z] = onFrame ? T.WALK : T.FORBID;
        }
      }
    } else if (li === 1) {
      // 环形+十字通道
      for (let x = 0; x < W; x++) {
        for (let z = 0; z < D; z++) {
          const onFrame = x < wall || x >= W - wall || z < wall || z >= D - wall;
          const cross = (Math.abs(z - Math.floor(D / 2)) <= 0) || (Math.abs(x - Math.floor(W / 2)) <= 0);
          f.type[x][z] = (onFrame || cross) ? T.WALK : T.FORBID;
        }
      }
    } else if (li === 2) {
      // 回字通道
      const ix = 5, ix2 = W - 6, iz = 5, iz2 = D - 6;
      for (let x = 0; x < W; x++) {
        for (let z = 0; z < D; z++) {
          const outer = x < wall || x >= W - wall || z < wall || z >= D - wall;
          const inner = ((x === ix || x === ix2) && (z >= iz && z <= iz2)) ||
                        ((z === iz || z === iz2) && (x >= ix && x <= ix2));
          f.type[x][z] = (outer || inner) ? T.WALK : T.FORBID;
        }
      }
      const mx = Math.floor(W / 2), mz = Math.floor(D / 2);
      for (let z = 2; z <= iz; z++) f.type[mx][z] = T.WALK;
      for (let z = iz2; z <= D - 3; z++) f.type[mx][z] = T.WALK;
      for (let x = 2; x <= ix; x++) f.type[x][mz] = T.WALK;
      for (let x = ix2; x <= W - 3; x++) f.type[x][mz] = T.WALK;
    } else {
      // 空白
      for (let x = 0; x < W; x++) {
        for (let z = 0; z < D; z++) {
          f.type[x][z] = T.FORBID;
        }
      }
    }
  },

  /**
   * 布局生成（4种布局 + 可选机房 + 末端，ID全局唯一）
   * @param floor 楼层对象
   * @param idx 布局索引
   * @param opts 选项
   */
  genLayout(floor: Floor, idx?: number, opts?: { withRoom?: boolean; endCount?: number; primary?: boolean }): void {
    opts = opts || {};
    const C = App.Config;
    const G = C.GRID;
    const T = C.T;
    const S = App.Store;
    const W = G.W, D = G.D;
    const f = floor;

    const fi = S.floors.indexOf(f);
    const li = (idx == null) ? S.layoutIdx : idx;

    // 生成可走管空间
    this.genWalkable(f, li);

    // 设备机房
    f.rooms = [];
    f.roomOrig.clear();
    if (opts.withRoom !== false) {
      const rcx = 0, rcz = Math.floor(D / 2);
      const room = {
        id: '设备机房',
        x: rcx,
        z: rcz,
        color: C.ROOM_COLOR,
        shape: C.ROOM_SHAPE_MAIN,
        floorIdx: fi,
        systemId: S.activeSystemId,
        added: false
      };
      f.rooms = [room];
      this.commitRoomCells(room);
    }

    // 末端
    f.ends = [];
    f.endOrig.clear();
    const spots = [
      { x: W - 1, z: Math.floor(D * 0.22) },
      { x: W - 1, z: Math.floor(D * 0.50) },
      { x: W - 1, z: Math.floor(D * 0.78) },
      { x: Math.floor(W * 0.50), z: D - 1 }
    ];
    const ec = opts.endCount != null ? opts.endCount : (opts.withRoom === false ? 2 : 4);

    for (let i = 0; i < ec; i++) {
      const sp = spots[i % spots.length];
      const num = opts.primary ? (i + 1) : (++S.endCounter);
      const e = {
        id: '末端' + num,
        x: sp.x,
        z: sp.z,
        color: C.END_COLORS[(num - 1) % C.END_COLORS.length],
        floorIdx: fi,
        systemId: S.activeSystemId,
        added: false
      };
      if (this.inB(e.x, e.z)) {
        const k = e.x + ',' + e.z;
        if (!f.endOrig.has(k)) f.endOrig.set(k, f.type[e.x][e.z]);
        f.type[e.x][e.z] = T.END;
      }
      f.ends.push(e);
    }

    if (opts.primary) {
      S.roomCounter = 1;
      S.endCounter = Math.max(S.endCounter, ec);
    }
  },
};

export default App.World;
export const World = App.World;
